import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Wallet, Pencil, Trash2, ArrowUpRight, ArrowDownRight, TrendingUp, ArrowLeftRight, DollarSign } from 'lucide-react';
import { useAccounts } from '@hooks/useAccounts';
import PageHeader from '@components/layout/PageHeader';
import Button from '@components/common/Button';
import Modal from '@components/common/Modal';
import Loading from '@components/common/Loading';
import EmptyState from '@components/common/EmptyState';
import PageTutorial from '@components/common/PageTutorial';
import AccountForm from '@components/accounts/AccountForm';
import TransferModal from '@components/accounts/TransferModal';
import { formatCurrency } from '@utils/formatters';
import { ACCOUNT_TYPE_LABELS } from '@utils/constants';
import { clsx } from 'clsx';

const Accounts = () => {
  const navigate = useNavigate();
  const {
    accounts,
    totalBalance,
    cashBalance,
    creditCardDebt,
    isLoading,
    createAccount,
    updateAccount,
    deleteAccount,
    transfer,
    isCreating,
    isUpdating,
    isDeleting,
    isTransferring,
  } = useAccounts();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isTransferOpen, setIsTransferOpen] = useState(false);
  const [transferDefaults, setTransferDefaults] = useState(null);

  const handleOpenModal = (account = null) => {
    setSelectedAccount(account);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedAccount(null);
    setIsModalOpen(false);
  };

  const handleSubmit = async (data) => {
    if (selectedAccount) {
      await updateAccount({ accountId: selectedAccount.accountId, data });
    } else {
      await createAccount(data);
    }
    handleCloseModal();
  };

  const handleDelete = async () => {
    if (selectedAccount) {
      await deleteAccount(selectedAccount.accountId);
      setIsDeleteModalOpen(false);
      setSelectedAccount(null);
    }
  };

  const openDeleteModal = (account) => {
    setSelectedAccount(account);
    setIsDeleteModalOpen(true);
  };

  const openTransfer = (defaults = {}) => {
    setTransferDefaults(defaults);
    setIsTransferOpen(true);
  };

  const closeTransfer = () => {
    setIsTransferOpen(false);
    setTransferDefaults(null);
  };

  if (isLoading) {
    return <Loading text="Carregando contas..." />;
  }

  const positiveAccounts = accounts.filter(a => a.currentBalance >= 0);
  const negativeAccounts = accounts.filter(a => a.currentBalance < 0);

  return (
    <div className="space-y-6 pb-8">
      <PageHeader
        title="Contas"
        subtitle="Gerencie suas contas bancárias e carteiras"
        action={
          <div className="flex items-center gap-2">
            <Button variant="secondary" leftIcon={<ArrowLeftRight size={18} />} onClick={() => openTransfer()}>
              Transferir
            </Button>
            <Button leftIcon={<Plus size={18} />} onClick={() => handleOpenModal()}>
              Nova Conta
            </Button>
          </div>
        }
      />

      <PageTutorial
        pageId="accounts"
        title="Suas contas"
        steps={[
          'Cadastre todas as suas contas bancarias, carteiras e investimentos',
          'O patrimonio total soma o saldo de todas as contas',
          'Passe o mouse sobre um card para ver as opcoes de editar e excluir',
        ]}
      />

      {/* Hero Summary */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-primary-900/80 to-slate-900 text-white shadow-2xl p-6">
        <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-primary-500/10 blur-2xl" />
        <div className="absolute -bottom-8 -left-8 w-36 h-36 rounded-full bg-primary-400/10 blur-2xl" />
        <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="text-white/50 text-sm font-medium mb-1">Patrimônio Total</p>
            <p className={clsx(
              'text-4xl font-bold tracking-tight',
              totalBalance >= 0 ? 'text-white' : 'text-red-400'
            )}>
              {formatCurrency(totalBalance)}
            </p>
            {creditCardDebt !== 0 && (
              <p className="text-white/60 text-sm mt-2">
                Em contas <span className="font-semibold text-emerald-300">{formatCurrency(cashBalance)}</span>
                {' · '}Cartão <span className="font-semibold text-red-300">{formatCurrency(creditCardDebt)}</span>
              </p>
            )}
            <p className="text-white/40 text-sm mt-2">
              {accounts.length} conta{accounts.length !== 1 ? 's' : ''} cadastrada{accounts.length !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="flex gap-6">
            <div className="text-center">
              <p className="text-white/40 text-xs mb-1">Positivas</p>
              <p className="text-emerald-400 font-bold text-lg">{positiveAccounts.length}</p>
            </div>
            <div className="w-px bg-white/10" />
            <div className="text-center">
              <p className="text-white/40 text-xs mb-1">Negativas</p>
              <p className="text-red-400 font-bold text-lg">{negativeAccounts.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Accounts Grid */}
      {accounts.length === 0 ? (
        <EmptyState
          icon={<Wallet size={32} />}
          title="Nenhuma conta cadastrada"
          description="Adicione suas contas bancárias para começar a controlar suas finanças."
          action={() => handleOpenModal()}
          actionLabel="Adicionar Conta"
        />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {accounts.map((account) => (
            <div
              key={account.accountId}
              className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden group hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => navigate(`/app/accounts/${account.accountId}`)}
            >
              {/* Color bar */}
              <div className="h-1.5" style={{ backgroundColor: account.color }} />

              <div className="p-5">
                {/* Header */}
                <div className="flex items-start justify-between mb-5">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center text-xl flex-shrink-0"
                      style={{ backgroundColor: `${account.color}18` }}
                    >
                      {account.icon}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white leading-tight">
                        {account.name}
                      </h3>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {ACCOUNT_TYPE_LABELS[account.type]}
                      </p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleOpenModal(account); }}
                      className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      title="Editar"
                    >
                      <Pencil size={14} className="text-gray-400" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); openDeleteModal(account); }}
                      className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                      title="Excluir"
                    >
                      <Trash2 size={14} className="text-red-400" />
                    </button>
                  </div>
                </div>

                {/* Balance */}
                <div>
                  <p className="text-xs text-gray-400 mb-1">Saldo atual</p>
                  <div className="flex items-center gap-1.5">
                    <p className={clsx(
                      'text-2xl font-bold',
                      account.currentBalance >= 0 ? 'text-gray-900 dark:text-white' : 'text-red-600'
                    )}>
                      {formatCurrency(account.currentBalance)}
                    </p>
                    {account.currentBalance >= 0
                      ? <ArrowUpRight size={16} className="text-emerald-500" />
                      : <ArrowDownRight size={16} className="text-red-500" />}
                  </div>
                </div>

                {account.type === 'credit_card' && (
                  <button
                    onClick={(e) => { e.stopPropagation(); openTransfer({ toAccountId: account.accountId }); }}
                    className="mt-4 w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 text-sm font-medium hover:bg-primary-100 dark:hover:bg-primary-900/30 transition-colors"
                  >
                    <DollarSign size={15} /> Pagar fatura
                  </button>
                )}
              </div>
            </div>
          ))}

          {/* Add new card */}
          <button
            onClick={() => handleOpenModal()}
            className="bg-white dark:bg-gray-800 rounded-2xl border-2 border-dashed border-gray-200 dark:border-gray-700 p-5 flex flex-col items-center justify-center gap-2 hover:border-primary-400 hover:bg-primary-50/30 dark:hover:bg-primary-900/10 transition-all group min-h-[140px]"
          >
            <div className="w-10 h-10 rounded-xl bg-gray-100 dark:bg-gray-700 group-hover:bg-primary-100 dark:group-hover:bg-primary-900/30 flex items-center justify-center transition-colors">
              <Plus size={20} className="text-gray-400 group-hover:text-primary-600 transition-colors" />
            </div>
            <p className="text-sm font-medium text-gray-400 group-hover:text-primary-600 transition-colors">
              Nova Conta
            </p>
          </button>
        </div>
      )}

      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={selectedAccount ? 'Editar Conta' : 'Nova Conta'}
        size="md"
      >
        <AccountForm
          account={selectedAccount}
          onSubmit={handleSubmit}
          onCancel={handleCloseModal}
          isLoading={isCreating || isUpdating}
        />
      </Modal>

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Excluir Conta"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setIsDeleteModalOpen(false)}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={handleDelete} isLoading={isDeleting}>
              Excluir
            </Button>
          </>
        }
      >
        <p className="text-gray-600 dark:text-gray-400">
          Tem certeza que deseja excluir a conta <strong>{selectedAccount?.name}</strong>?
          Esta ação não pode ser desfeita.
        </p>
      </Modal>

      <TransferModal
        isOpen={isTransferOpen}
        onClose={closeTransfer}
        accounts={accounts}
        transfer={transfer}
        isTransferring={isTransferring}
        defaultValues={transferDefaults}
      />
    </div>
  );
};

export default Accounts;
